import React, { useEffect, useRef, useState } from 'react';
import Parcel from 'single-spa-react/parcel';
import { mountRootParcel } from 'single-spa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { StateParams } from '../../store/reducers';
import { LanguageCodes } from '../../models';
import { patientTempLogin, setLanguagePreference } from '../../store/actions/account.action';

// System is provided globally by the SystemJS runtime loaded in index.html.
// Accessed via window to avoid CRA's no-restricted-properties lint rule on System.import.
const systemImport = (name: string): Promise<any> =>
    (window as any).System.import(name);

const CHECK_IN_LOGIN_SOURCE = 'self-check-in-link';

const normalizeLanguageCode = (language?: string): LanguageCodes | undefined => {
    if (!language) return undefined;

    const normalizedLanguage = language.trim().toLowerCase().replace('-', '_');
    const languageMap: Record<string, LanguageCodes> = {
        en_us: LanguageCodes.EN_US,
        es: LanguageCodes.ES,
        ru: LanguageCodes.RU,
        pl: LanguageCodes.PL,
        hi: LanguageCodes.HI,
        gu: LanguageCodes.GU,
        ar: LanguageCodes.AR,
    };

    return languageMap[normalizedLanguage];
};

const getPreferredLanguageFromSession = (patientId: string): LanguageCodes | undefined => {
    const patientFormKeyPrefix = `pf_${patientId}_`;
    const patientFormKeys = Object.keys(sessionStorage).filter(key => key.startsWith(patientFormKeyPrefix));

    for (const key of patientFormKeys) {
        const storedValue = sessionStorage.getItem(key);
        if (!storedValue) continue;

        try {
            const parsedValue = JSON.parse(storedValue);
            const rawPreferredLanguage = parsedValue?.personal_information?.preferred_language;
            if (!rawPreferredLanguage) continue;

            const preferredLanguage = normalizeLanguageCode(rawPreferredLanguage);
            return preferredLanguage;
        } catch (error) {
            console.warn(`PatientFormsParcel: failed to parse sessionStorage entry ${key}`, error);
        }
    }

    console.warn('PatientFormsParcel: preferred language was not found in patient form storage', {
        patientId,
        patientFormKeys,
    });
    return undefined;
};

const PatientFormsParcel: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { patientId } = useParams<{ patientId: string }>();
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/screenerQuestion';
    const [isComplete, setIsComplete] = useState(false);
    const completionHandled = useRef(false);

    const jwt = useSelector((state: StateParams) => state.account.jwt);
    const provider = useSelector((state: StateParams) => state.account.provider);
    const practiceId = useSelector((state: StateParams) => state.account.practiceId);
    const loginSource = useSelector((state: StateParams) => state.account.loginSource);
    const treatmentId = useSelector((state: StateParams) => state.signup.treatmentId);
    const appointmentId = useSelector((state: StateParams) => state.signup.appointmentId);
    const checkInId = useSelector((state: StateParams) => state.signup.checkInId);
    const checkInVerificationResponse = useSelector((state: StateParams) => state.signup.checkInVerificationResponse);

    const resolvedPracticeId = practiceId ?? checkInVerificationResponse?.patientCheckInRecord?.practiceId;
    const resolvedLoginSource = loginSource ?? CHECK_IN_LOGIN_SOURCE
    const resolvedAppointmentId = appointmentId ?? checkInVerificationResponse?.appointment?.id;
    const resolvedCheckInId = checkInId ?? checkInVerificationResponse?.patientCheckInRecord?.id;

    useEffect(() => {
        if (isComplete && (jwt || provider?.jwt)) {
            navigate(returnTo, { replace: true });
        }
    }, [isComplete, jwt, provider?.jwt, navigate, returnTo]);

    if (!patientId || !resolvedPracticeId || !resolvedLoginSource) {
        console.error('PatientFormsParcel: missing required context (patientId, practiceId, loginSource)');
        return null;
    }

    const onFormCompletion = () => {
        if (completionHandled.current) return;
        completionHandled.current = true;
        const preferredLanguage = getPreferredLanguageFromSession(patientId);
        if (preferredLanguage) {
            console.log('PatientFormsParcel: dispatching preferred language', preferredLanguage);
            dispatch(setLanguagePreference({ languagePreference: preferredLanguage }));
        } else {
            console.warn('PatientFormsParcel: preferred language was not updated');
        }

        localStorage.removeItem('sj:patient-session-id');
        Object.keys(sessionStorage)
            .filter(key => key.startsWith('pf_'))
            .forEach(key => sessionStorage.removeItem(key));
        setIsComplete(true);

        if (!jwt && !provider?.jwt) {
            dispatch(patientTempLogin({
                patientId,
                practiceId: resolvedPracticeId,
                loginSource: resolvedLoginSource,
                ...(resolvedAppointmentId && { appointmentId: resolvedAppointmentId }),
            }));
        }
    };

    return (
        <Parcel
            config={() => systemImport('@Smalljoys/PatientForms')}
            mountParcel={mountRootParcel}
            patientId={patientId}
            practiceId={resolvedPracticeId}
            loginSource={resolvedLoginSource}
            {...(treatmentId && { treatmentId })}
            {...(resolvedAppointmentId && { appointmentId: resolvedAppointmentId })}
            {...(resolvedCheckInId && { checkInId: resolvedCheckInId })}
            onComplete={onFormCompletion}
        />
    );
};

export default PatientFormsParcel;
